import { type ReactElement } from 'react'
import { isDefined, isEmpty, isNotEmpty, type DashboardDataWithEditable } from '@terros/common'
import { Group, Stack, Text, UnstyledButton } from '@mantine/core'
import { useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '@components'
import { DashboardRowMenu } from '../DashboardRowMenu'

type Props = {
  /** The scope's own hustle dashboard, or `undefined` when it hasn't built one. */
  own: DashboardDataWithEditable | undefined
  /** The most specific one already reaching the viewer, named in the detail line. */
  inherited: DashboardDataWithEditable | undefined
  isCreating: boolean
  /** Delete in flight for the own hustle, so the menu can spin. */
  deleting: boolean
  onDelete: (dashboard: DashboardDataWithEditable) => void
  onDuplicate: (dashboard: DashboardDataWithEditable) => void
  onOpen: (dashboard: DashboardDataWithEditable) => void
  onCreate: () => void
}

/**
 * The pinned Hustle row. Clicking it opens the scope's own hustle dashboard,
 * creating one first when there isn't one — so the card explains what the Hustle
 * widget is rather than leaving an empty section.
 */
export function HustleCard({
  own,
  inherited,
  isCreating,
  deleting,
  onDelete,
  onDuplicate,
  onOpen,
  onCreate,
}: Props): ReactElement {
  const { t } = useTranslation()
  const ownName = own?.name?.trim()
  const inheritedName = inherited?.name?.trim()

  const detail = (): string => {
    if (isDefined(own)) return t('Always first on Home — the main drivers of your business.')
    if (isNotEmpty(inheritedName)) {
      return t('Home currently shows "{{name}}". Build your own to replace it.', { name: inheritedName })
    }
    return t('Always first on Home. Click to build yours.')
  }

  return (
    <div className='border-default-border bg-brand-light p-3xl rounded-2xl border'>
      <Group gap='md' wrap='nowrap'>
        <UnstyledButton
          className='min-w-0 flex-1'
          disabled={isCreating}
          onClick={() => (own ? onOpen(own) : onCreate())}
        >
          <Group gap='md' wrap='nowrap'>
            <MaterialSymbolIcon icon='monitoring' size='xl' />
            <Stack className='text-start' gap={2}>
              <Text fw={600}>{isEmpty(ownName) ? t('Hustle Widget') : ownName}</Text>
              <Text c='dimmed' size='sm'>
                {detail()}
              </Text>
            </Stack>
            <MaterialSymbolIcon className='ml-auto' icon='chevron_right' size='md' />
          </Group>
        </UnstyledButton>
        {own?.editable === true && (
          <DashboardRowMenu dashboard={own} isBusy={deleting} onDelete={onDelete} onDuplicate={onDuplicate} />
        )}
      </Group>
    </div>
  )
}
